/**
 * RenderingClipPlayer — render-time animation built from admin-curated
 * 10s MP4 clips. Replaces the legacy ChameleonStage (static JPGs +
 * scripted character popups).
 *
 * Behaviour:
 *   • On mount, fetches the matching batch from `/api/animation/batch`
 *     using the song's prompt/description/style as genre signal.
 *   • Plays the resolved clip sequence back-to-back with embedded
 *     audio. Uses two layered <video> elements so we can preload the
 *     next clip while the current one is playing — eliminates the
 *     1-2 frame black flash you get when swapping `src` on a single
 *     element.
 *   • Song-done-early: parent passes `songCompleted={true}` once the
 *     song finishes rendering. We finish the current clip, switch to
 *     the configured `outro_clip`, then call `onAnimationDone` so the
 *     parent can transition to the result page.
 *   • Graceful fallback: if no batch matches and there are no
 *     defaults, falls back to a lightweight skeleton spinner so the
 *     app never has a blank loading state.
 *   • Auto-loops the sequence if the song takes longer than the
 *     batch length — re-starts from clip 0 so the experience never
 *     dies mid-render.
 *   • Mute toggle for users who don't want clip audio.
 *
 * Props:
 *   • elapsedSeconds   — seconds since render started (parent owns the timer)
 *   • estimatedMinutes — e.g. "2-4", rendered in the "TYPICAL" badge
 *   • songCompleted    — flips true when song.status === 'completed'
 *   • genre / prompt / description — passed to the picker as routing hints
 *   • onAnimationDone  — called once the outro clip finishes after `songCompleted`
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Volume2, VolumeX } from 'lucide-react';
import { apiFetch } from '../App';

export default function RenderingClipPlayer({
  elapsedSeconds = 0,
  estimatedMinutes = '2-4',
  songCompleted = false,
  genre,
  prompt,
  description,
  onAnimationDone,
}) {
  const [batch, setBatch] = useState(null);
  const [clips, setClips] = useState([]);
  const [outroClip, setOutroClip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(false);
  const [playingOutro, setPlayingOutro] = useState(false);

  // Two layered <video> refs — `current` plays, `next` is preloaded
  // behind it so the swap is instantaneous when `onEnded` fires.
  const currentVidRef = useRef(null);
  const nextVidRef = useRef(null);

  // Synchronously-updated ref of `songCompleted` so the `onEnded`
  // handler ALWAYS reads the latest value, regardless of React's
  // render commit timing. (Still useful for graceful handling if
  // the effect below misses an edge case.)
  const songCompletedRef = useRef(songCompleted);
  songCompletedRef.current = songCompleted;

  // IMMEDIATE transition the moment `songCompleted` flips true:
  //   • If an outro clip is configured → jump straight to outro
  //   • If no outro is configured → fire onAnimationDone immediately
  //     so the parent can unmount the player and reveal the song
  // Rationale: the user has waited 2-4 minutes for their song to
  // render. The moment it's ready, they want to HEAR IT — not sit
  // through the rest of a filler clip. Cutting straight is both
  // snappier AND removes a whole class of event-timing bugs the
  // "finish current clip first" approach kept introducing.
  //
  // CRITICAL: we MUST wait for the batch fetch (`loading=false`)
  // before deciding to bail with `onAnimationDone()`. If we don't,
  // a remount of this component (e.g. when SongDetail unmounts the
  // player for a single render frame during the status transition
  // and immediately remounts it once `outroPending` flips true)
  // sees `outroClip=null` (still fetching) + `songCompleted=true`
  // and bails INSTANTLY — the outro never plays even though one is
  // configured. The `!loading` guard closes that race.
  useEffect(() => {
    if (!songCompleted) return;
    if (loading) return;
    if (outroClip && !playingOutro) {
      setPlayingOutro(true);
    } else if (!outroClip && onAnimationDone) {
      onAnimationDone();
    }
  }, [songCompleted, outroClip, playingOutro, onAnimationDone, loading]);

  // Fetch the batch ONCE on first mount based on the song's genre/prompt
  // hints. We deliberately ignore prop changes after the first fetch —
  // when a song transitions from `processing` to `completed`, Suno often
  // back-fills extra metadata (description, mood) which would otherwise
  // re-trigger this effect, refetch a potentially-different batch with
  // a different `clips.length`, and make the visible clip jump
  // unexpectedly (e.g. wrap back to clip[0] via modulo) right before
  // the outro fires. Locking the batch on first load eliminates that.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (genre) params.append('genre', genre);
        if (prompt) params.append('prompt', prompt);
        if (description) params.append('description', description);
        const { data } = await apiFetch(`/animation/batch?${params.toString()}`);
        if (cancelled) return;
        setBatch(data.batch);
        setClips(data.clips || []);
        setOutroClip(data.outro_clip);
      } catch {
        // Picker is non-essential — fall through to the fallback spinner.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-play the current clip whenever index changes.
  useEffect(() => {
    const el = currentVidRef.current;
    if (!el || clips.length === 0) return;
    el.muted = muted;
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay may be blocked */ });
  }, [index, clips.length, muted, playingOutro]);

  // The current clip — either the indexed sequence clip or the outro.
  const currentClip = useMemo(() => {
    if (playingOutro && outroClip) return outroClip;
    if (clips.length === 0) return null;
    return clips[index % clips.length];
  }, [clips, index, playingOutro, outroClip]);

  // The next clip — used by the preloader. When playing the outro,
  // there's no "next".
  const nextClip = useMemo(() => {
    if (playingOutro || clips.length === 0) return null;
    // If song completed and we have an outro defined, the next visible
    // video will be the outro — preload it so the swap is instant.
    if (songCompleted && outroClip) return outroClip;
    return clips[(index + 1) % clips.length];
  }, [clips, index, songCompleted, outroClip, playingOutro]);

  // When the current clip ends, advance. If song is done and we have
  // an outro, jump to outro; if we're already on the outro, signal
  // animation-done. If song is done and there is NO outro configured,
  // signal animation-done immediately at the next clip boundary so the
  // caller can unmount the player.
  //
  // Reads `songCompletedRef.current` — synced inline during render
  // (see ref definition above). This avoids the React 18 batching
  // race that caused a clip[0] hop right before the outro.
  const onClipEnded = useCallback(() => {
    if (playingOutro) {
      if (onAnimationDone) onAnimationDone();
      return;
    }
    if (songCompletedRef.current) {
      if (outroClip) {
        setPlayingOutro(true);
        return;
      }
      // Song finished and no outro defined — end the animation gracefully.
      if (onAnimationDone) onAnimationDone();
      return;
    }
    setIndex((i) => (i + 1) % Math.max(1, clips.length));
  }, [playingOutro, onAnimationDone, outroClip, clips.length]);

  const mmss = `${Math.floor(elapsedSeconds / 60)}:${String(elapsedSeconds % 60).padStart(2, '0')}`;

  // ----- Fallback spinner (no batch available) ----------------------------
  if (!loading && (!currentClip || !currentClip.video_url)) {
    return (
      <div className="relative w-full aspect-video bg-zinc-900 rounded-lg overflow-hidden flex items-center justify-center" data-testid="render-fallback-spinner">
        <Loader2 className="h-12 w-12 animate-spin text-amber-500" />
        <div className="absolute bottom-3 left-3 text-xs text-zinc-400 font-mono">
          {mmss} ELAPSED
        </div>
        <div className="absolute bottom-3 right-3 text-xs text-zinc-400">
          ~{estimatedMinutes} min TYPICAL
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="relative w-full aspect-video bg-zinc-900 rounded-lg overflow-hidden flex items-center justify-center" data-testid="render-loading">
        <Loader2 className="h-12 w-12 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden" data-testid="render-clip-player">
      {/* Current clip — fully visible, on top.
          `key` forces React to fully unmount/remount the <video> element
          when the playing clip changes (especially when crossing from
          a normal clip → outro). Without it React reuses the DOM node,
          which can leave the browser playing the previously-buffered
          clip for a frame or two before the new src loads. */}
      <video
        key={currentClip.clip_id}
        ref={currentVidRef}
        src={currentClip.video_url}
        autoPlay
        playsInline
        muted={muted || currentClip.audio_enabled === false}
        onEnded={onClipEnded}
        className="absolute inset-0 w-full h-full object-cover"
        data-testid={`render-clip-current-${currentClip.clip_id}`}
      />
      {/* Next clip — preloaded behind the current one. `display:none`
          would block preloading on some browsers; use opacity:0 +
          pointer-events-none instead so the network fetch still runs. */}
      {nextClip && nextClip.video_url && (
        <video
          ref={nextVidRef}
          src={nextClip.video_url}
          preload="auto"
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none"
          data-testid={`render-clip-preload-${nextClip.clip_id}`}
        />
      )}

      {/* Overlay UI */}
      <div className="absolute top-2 left-2 bg-black/50 backdrop-blur px-2 py-1 rounded text-[10px] text-amber-300 font-mono uppercase tracking-wide">
        {batch?.name || 'Rendering'}
      </div>
      <button
        type="button"
        onClick={() => setMuted((m) => !m)}
        className="absolute top-2 right-2 bg-black/50 backdrop-blur p-1.5 rounded text-zinc-200 hover:text-white"
        title={muted ? 'Unmute clips' : 'Mute clips'}
        data-testid="render-clip-mute-toggle"
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
      <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur px-2 py-1 rounded text-xs text-white font-mono">
        {mmss} <span className="text-zinc-400 ml-1">ELAPSED</span>
      </div>
      <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur px-2 py-1 rounded text-xs text-zinc-300">
        ~{estimatedMinutes} min TYPICAL
      </div>
    </div>
  );
}
